import React, { Component } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import DrawRectangleMode from "mapbox-gl-draw-rectangle-mode";
import {
  FaDrawPolygon,
  FaRegCircle,
  FaRegSquare,
  FaTrash,
} from "react-icons/fa";

import DrawCircleMode from "./DrawCircleModes";
// import DrawRectangle from "@mapbox/mapbox-gl-draw-rectangle-mode";
// import DrawCircle from "mapbox-gl-draw-circle";
// import MapboxDraw from "@mapbox/mapbox-gl-draw";
import {
  CircleMode,
  DragCircleMode,
  DirectMode,
  SimpleSelectMode,
} from "maplibre-gl-draw-circle";
import LiveTrackingStore from "../store/LiveTrackingStore";
import * as turf from "@turf/turf";

// 1. Maintain your existing compatibility overrides
MapboxDraw.constants.classes.CONTROL_BASE = "maplibregl-ctrl";
MapboxDraw.constants.classes.CONTROL_PREFIX = "maplibregl-ctrl-";
MapboxDraw.constants.classes.CONTROL_GROUP = "maplibregl-ctrl-group";
MapboxDraw.constants.classes.CANVAS = "maplibregl-canvas";

class DrawControl extends Component {
  constructor(prop, context) {
    super(prop);
    this.state = {};
    this.liveTrackingStore = new LiveTrackingStore(this);
    this.currentDrawShape = null;
    this.featureMeta = {};
  }

  componentDidMount() {
    const { map, position, onDrawReady, ...drawOptions } = this.props;
    if (!map) return;

    // 2. Define a compatible style array to fix the dasharray error
    const customStyles = [
      // ... include other default draw layers if needed,
      // but the critical fix is for the 'gl-draw-lines' layer:
      {
        id: "gl-draw-line-inactive",
        type: "line",
        filter: [
          "all",
          ["==", "active", "false"],
          ["==", "$type", "LineString"],
        ],
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#3bb2d0",
          "line-width": 3,
        },
      },
      {
        id: "gl-draw-line-active",
        type: "line",
        filter: [
          "all",
          ["==", "active", "true"],
          ["==", "$type", "LineString"],
        ],
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#fbb03b",
          "line-dasharray": ["literal", [0.2, 2]], // FIX: Use ["literal", [...]]
          "line-width": 3,
        },
      },
      // Add standard polygon fill styles
      {
        id: "gl-draw-polygon-fill-inactive",
        type: "fill",
        filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"]],
        paint: {
          "fill-color": "#FF0000",
          "fill-outline-color": "#FF0000",
          "fill-opacity": 0.4,
        },
      },
      {
        id: "gl-draw-polygon-fill-active",
        type: "fill",
        filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
        paint: {
          "fill-color": "#0096FF",
          "fill-outline-color": "#0096FF",
          "fill-opacity": 0.5,
        },
      },
      {
        id: "gl-draw-point-active",
        type: "circle",
        filter: ["all", ["==", "$type", "Point"], ["==", "active", "true"]],
        paint: {
          "circle-radius": 7,
          "circle-color": "#fbb03b",
        },
      },
      {
        id: "gl-draw-point-inactive",
        type: "circle",
        filter: ["all", ["==", "$type", "Point"], ["==", "active", "false"]],
        paint: {
          "circle-radius": 5,
          "circle-color": "#fbb03b",
        },
      },
      {
        id: "gl-draw-circle-radius-handle",
        type: "circle",
        filter: [
          "all",
          ["==", "active", "true"],
          ["==", "user_isCircle", true],
          ["==", "$type", "Point"],
        ],
        paint: {
          "circle-radius": 6,
          "circle-color": "#fbb03b",
          "circle-stroke-width": 2,
          "circle-stroke-color": "#fff",
        },
      },
      {
        id: "gl-draw-circle-vertex-hidden",
        type: "circle",
        filter: [
          "all",
          ["==", "$type", "Point"],
          ["==", "meta", "vertex"],
          // ["==", ["get", "isCircle"], true],
          ["==", "isCircle", true],
        ],
        paint: {
          "circle-radius": 0,
        },
      },
      // Note: You can find the full default theme array in mapbox-gl-draw docs
      // and apply the ["literal", ...] fix to any layer using line-dasharray.
    ];

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      // defaultMode: "simple_select",
      styles: customStyles,
      userProperties: true,

      modes: {
        ...MapboxDraw.modes,
        draw_circle: CircleMode,
        drag_circle: DragCircleMode,
        // Use the circle-specific select modes
        direct_select: DirectMode,
        simple_select: SimpleSelectMode,
        draw_rectangle: DrawRectangleMode,
      },
    });

    map.addControl(this.draw, position);
    // draw.changeMode("draw_circle", { initialRadiusInKm: 0.5 });

    const container = map.getCanvasContainer();
    container.classList.add("mapboxgl-canvas-container");
    map.getCanvas().classList.add("mapboxgl-canvas");
    onDrawReady?.(this.draw);

    map.on("draw.create", async (e) => {
      const feature = e.features[0];
      const featureId = feature.id;

      this.draw.setFeatureProperty(
        feature.id,
        "shapeType",
        this.currentDrawShape
      );

      if (this.currentDrawShape === "circle") {
        this.draw.setFeatureProperty(feature.id, "isCircle", true);
      }

      if (this.currentDrawShape === "rectangle") {
        this.draw.setFeatureProperty(feature.id, "isRectangle", true);
      }

      this.featureMeta[feature.id] = {
        shapeType: this.currentDrawShape,
      };

      feature.properties = {
        ...feature.properties,
        shapeType: this.currentDrawShape,
        isCircle: this.currentDrawShape === "circle",
        isRectangle: this.currentDrawShape === "rectangle",
      };

      const geofenceData = await this.preparePayload(this.draw.get(feature.id));

      // console.log(geofenceData, "geofenceData");

      // SEND TO LiveTracking.js
      this.props.onGeofenceCreate?.(geofenceData);

      // Enable edit mode
      setTimeout(() => {
        if (this.draw.get(featureId)) {
          this.draw.changeMode("direct_select", { featureId });
        }
      }, 0);
    });

    map.on("draw.update", async (e) => {
      const featureId = e.features[0]?.id;
      if (!featureId) return;

      const feature = this.draw.get(featureId);
      if (!feature) return;

      let shapeType =
        this.featureMeta[featureId]?.shapeType ||
        feature.properties?.shapeType ||
        "polygon";

      console.log(shapeType, "shapeType");
      // Re-attach (optional, but useful)
      this.draw.setFeatureProperty(featureId, "shapeType", shapeType);

      feature.properties = {
        ...feature.properties,
        shapeType,
        isCircle: shapeType === "circle",
        isRectangle: shapeType === "rectangle",
      };

      const geofenceData = await this.preparePayload(feature);
      this.props.onGeofenceUpdate?.(geofenceData);
    });

    map.on("draw.delete", (e) => {
      e.features.forEach((f) => {
        delete this.featureMeta[f.id];
      });
    });

    // map.on("draw.update", async (e) => {
    //   // const feature = e.features[0];
    //   // if (!feature) return;

    //   // const geometry = feature.geometry;
    //   // const featureId = feature.id;

    //   const featureId = e.features[0]?.id;

    //   console.log(featureId, "featureId");
    //   if (!featureId) return;

    //   // ✅ ALWAYS get fresh feature from Draw
    //   const feature = this.draw.get(featureId);
    //   if (!feature) return;

    //   // if (!feature.properties?.shapeType) {
    //   //   const inferredType = this.inferShapeType(feature);

    //   //   this.draw.setFeatureProperty(featureId, "shapeType", inferredType);
    //   //   feature.properties.shapeType = inferredType;
    //   // }

    //   let shapeType = feature.properties?.shapeType;

    //   console.log(shapeType, "shapeType");

    //   console.log(
    //     feature.properties?.isRectangle,
    //     "feature.properties?.isRectangle"
    //   );

    //   if (!shapeType) {
    //     if (feature.properties?.isRectangle) {
    //       shapeType = "rectangle";
    //     } else if (feature.properties?.isCircle) {
    //       shapeType = "circle";
    //     } else {
    //       // fallback only for legacy data
    //       shapeType = "polygon";
    //     }

    //     this.draw.setFeatureProperty(featureId, "shapeType", shapeType);
    //   }
    //   console.log(shapeType, "shapeType");
    //   // Only infer ONCE (for legacy / imported data)
    //   // if (!shapeType) {
    //   //   shapeType = this.inferShapeType(feature);

    //   //   this.draw.setFeatureProperty(featureId, "shapeType", shapeType);
    //   // }

    //   // Ensure runtime object also has it
    //   feature.properties = {
    //     ...feature.properties,
    //     shapeType,
    //     isCircle: shapeType === "circle",
    //     isRectangle: shapeType === "rectangle",
    //   };

    //   const geofenceData = await this.preparePayload(feature);

    //   console.log("Geofence Updated:", geofenceData);

    //   //  Send to LiveTracking.js
    //   this.props.onGeofenceUpdate?.(geofenceData);
    // });
  }

  inferShapeType = (feature) => {
    if (feature.properties?.isCircle) return "circle";

    if (feature.geometry.type === "Polygon") {
      const coords = feature.geometry.coordinates?.[0];
      if (!coords) return "polygon";

      // Rectangle heuristic (4 points + closed)
      if (coords.length === 5) return "rectangle";

      return "polygon";
    }

    return "polygon";
  };

  getShapeCenter = (feature) => {
    const shapeType = feature.properties?.shapeType;
    const geometry = feature.geometry;

    //  CIRCLE
    if (shapeType === "circle" && feature.properties?.center) {
      const [lng, lat] = feature.properties.center;
      return { lat, lng };
    }

    //  POLYGON / RECTANGLE
    if (geometry.type === "Polygon") {
      const centroid = turf.centroid(feature);
      const [lng, lat] = centroid.geometry.coordinates;
      return { lat, lng };
    }

    return null;
  };

  componentWillUnmount() {
    const { map } = this.props;
    if (map && this.draw) {
      map.removeControl(this.draw);
    }
  }

  activatePolygon = () => {
    this.currentDrawShape = "polygon";
    this.draw?.changeMode("draw_polygon");
  };

  activateCircle = () => {
    this.currentDrawShape = "circle";
    this.draw?.changeMode("draw_circle");
  };

  activateRectangle = () => {
    this.currentDrawShape = "rectangle";

    this.draw?.changeMode("draw_rectangle");
  };

  activateTrash = () => {
    const selected = this.draw.getSelectedIds();
    selected.forEach((id) => this.draw.delete(id));
  };

  preparePayload = async (feature) => {
    console.log(feature, "feature");
    const featureId = feature.id;
    const geometry = feature.geometry;
    // let shapeType = e.features[0].properties?.shapeType;
    const shapeType = feature.properties?.shapeType;

    console.log(shapeType, "shapeType");
    let radiusInKm = null;
    let circleCenter = null;

    let drawingBounds = "";

    if (shapeType === "circle") {
      radiusInKm = feature.properties?.radiusInKm || null;
      circleCenter = feature.properties?.center || null;

      console.log(radiusInKm, "radiusInKm");
      console.log(circleCenter, "circleCenter");
      if (!circleCenter) return null;
      // const coords = geometry.coordinates[0];

      drawingBounds = `${circleCenter[1]},${circleCenter[0]}`;
      // drawingBounds = coords.map(([lng, lat]) => `${lat},${lng},0`).join(",");
    } else if (geometry.type === "Polygon" && geometry.coordinates?.length) {
      const coords = geometry.coordinates[0];
      drawingBounds = coords.map(([lng, lat]) => `${lat},${lng},0`).join(",");
    }

    const center = this.getShapeCenter(feature);

    console.log(shapeType, center.lat, center.lng, "aessssssssssss");
    if (!center) return;
    const address = await this.liveTrackingStore.getAddressUsingLatLong(
      shapeType,
      center.lat,
      center.lng
      // shape.radius
    );
    return {
      id: featureId,
      type: shapeType,
      // center: { lat, lng },
      geometry,
      radius: radiusInKm,
      circleCenter, // [lng, lat] if circle
      drawingBounds,
      address,
    };
  };

  render() {
    return (
      <div
        style={{
          position: "absolute",
          zIndex: 10,
          padding: 8,
          // borderRadius: 4,
        }}
      >
        <button className="draw-btn" onClick={this.activatePolygon}>
          <FaDrawPolygon />
        </button>

        <button className="draw-btn" onClick={this.activateCircle}>
          <FaRegCircle />
        </button>

        <button className="draw-btn" onClick={this.activateRectangle}>
          <FaRegSquare />
        </button>

        <button className="draw-btn" onClick={this.activateTrash}>
          <FaTrash />
        </button>
      </div>
    );
  }
}

export default DrawControl;
